import { apiFetch } from '@/utils'
import type { PaymentMethodType } from '@/types/payment-methods'

// ─── Transaction types ──────────────────────────────────────────────
export type TransactionType = 'membership' | 'entry_pass' | 'order'

// ─── Charge request ─────────────────────────────────────────────────
// Common fields every charge sends, plus type-specific context
export interface ChargeRequest {
	/** What kind of purchase this is */
	transactionType: TransactionType
	/** TOKEN or CARD */
	paymentMethodType: PaymentMethodType
	/** USD amount to charge */
	amount: number
	/** Required for CARD payments — the saved card's ID */
	paymentMethodId?: number
	/** User's Firebase UID */
	userUid?: string
	/** User's wallet address (needed for TOKEN payments + identity) */
	walletAddress?: string
	/** TOKEN-only: EIP-712 signature */
	signature?: string
	/** TOKEN-only: the meta-tx forward request / transfer request object */
	forwardRequest?: any
	/** TOKEN-only: nonce from the contract (orders use this) */
	nonce?: number
	/** Optional discount */
	discountId?: number
	discountCode?: string | null
	discountAmount?: number
	/**
	 * Type-specific context for the backend.
	 * Memberships: membershipTemplateId, customerAddress, userUid, waivers[]
	 * Entry passes: entryPassTemplateId, checkInDate, eventDate
	 * Orders: orderId, total, subtotal, tax_amount, taxBreakdown, items, location
	 */
	context: Record<string, any>
}

// ─── Charge response ────────────────────────────────────────────────
export interface ChargeResponse {
	success: boolean
	txHash?: string
	transactionId?: number
	orderId?: string
	message?: string
	[key: string]: any
}

// ─── The single charge call ─────────────────────────────────────────
export async function chargePayment(req: ChargeRequest): Promise<ChargeResponse> {
	const {
		transactionType,
		paymentMethodType,
		amount,
		paymentMethodId,
		userUid,
		walletAddress,
		signature,
		forwardRequest,
		nonce,
		discountId,
		discountCode,
		discountAmount,
		context,
	} = req

	const body: Record<string, any> = {
		transactionType,
		paymentMethodType,
		amount,
		...context,
	}

	// Identity
	if (userUid) body.userUid = userUid
	if (walletAddress) {
		body.walletAddress = walletAddress
		body.customerAddress = walletAddress
	}

	// CARD-specific
	if (paymentMethodType === 'CARD' && paymentMethodId != null) {
		body.paymentMethodId = paymentMethodId
	}

	// TOKEN-specific
	if (paymentMethodType === 'TOKEN') {
		if (signature) body.signature = signature
		if (forwardRequest) body.forwardRequest = forwardRequest
		if (nonce != null) body.nonce = nonce
	}

	// Discount
	if (discountId != null) body.discountId = discountId
	if (discountCode != null) body.discountCode = discountCode
	if (discountAmount != null) body.discountAmount = discountAmount

	const res = await apiFetch('/v1/billing/charge', {
		method: 'POST',
		body: JSON.stringify(body),
		withCredentials: true,
	})

	const json = await res.json().catch(() => ({}))

	// HTTP-level failure
	if (!res.ok) {
		throw new Error(json.message || `Payment failed (${res.status})`)
	}

	// Backend returns 200 with success: false for declined transactions
	if (json.success === false) {
		throw new Error(json.message || 'Payment was declined')
	}

	return json as ChargeResponse
}
